import { Hubs, OrgHubs } from 'utils/gloo-types';
import classNames from 'classnames';
import FavoriteButton from 'pages/_AdminAuthoringPages/_components/FavoriteButton';
import Icon from 'components/atoms/Icon';
import Markdown from 'components/atoms/Markdown';
import Typography from 'components/atoms/Typography';
import PlayVideoButton from 'components/molecules/PlayVideoButton';
import { useEffect } from 'react';
import { REDUX_ACTIONS } from 'utils/constants';
import { getVideoThumbnail, isVideo } from 'utils/discover';
import { useThunkDispatch } from 'utils/reduxHook';
import { isHexColorLight } from 'utils/common';
import { HubPreviewView } from '..';

interface HubHeaderProps {
  hub: Hubs;
  orgHub: OrgHubs;
  favoriteHub: () => void;
  unfavoriteHub: () => void;
  previewView?: HubPreviewView;
}

const HubHeaderPreview = ({ hub, orgHub, favoriteHub, unfavoriteHub, previewView }: HubHeaderProps) => {
  const dispatch = useThunkDispatch();

  const hubTitleColor: React.CSSProperties = {
    color: hub.titleColor ?? '#000',
  };
  const hubDescriptionColor: React.CSSProperties = {
    color: hub.descriptionColor ?? '#000',
  };
  const hubBackgroundColor: React.CSSProperties = {
    backgroundColor: hub.backgroundColor ?? '#FFF',
  };
  const hubButtonBackgroundColor: React.CSSProperties = {
    backgroundColor: hub.titleColor ?? '#FFF',
  };
  if (isHexColorLight(hubButtonBackgroundColor.backgroundColor)) {
    hubButtonBackgroundColor.color = '#000';
  }

  const hasVideoThumbnail = isVideo(hub.headerImageUrl);
  const thumbnail = hasVideoThumbnail ? getVideoThumbnail(hub.headerImageUrl) : hub.headerImageUrl;

  const branding = {
    textColor: hubDescriptionColor.color,
    titleColor: hubTitleColor.color,
    backgroundColor: hubBackgroundColor.backgroundColor,
  };
  useEffect(() => {
    dispatch({ type: REDUX_ACTIONS.SET_BRANDING, branding });
  }, [branding]);

  const isDesktopView = previewView === HubPreviewView.Desktop;
  const isMobileView = previewView === HubPreviewView.Mobile;

  return (
    <div className="pt-10" style={hubBackgroundColor}>
      <div
        className={classNames('py-5 px-5', {
          'md:mx-6 lg:mx-10 xl:mx-24': !previewView,
          'mx-24': isDesktopView,
        })}
      >
        <button
          type="button"
          data-durable-id="hubsback"
          data-intercom-target="hubsback"
          className={classNames('text-neutral-900 flex pb-10', {
            'pt-5 md:pt-10 xl:-ml-2': !previewView,
            'pt-10 -ml-2': isDesktopView,
            'pt-5': isMobileView,
          })}
          style={hubDescriptionColor}
          onClick={undefined}
        >
          <Icon name="arrow-left" className="mr-5" />
          Back
        </button>
        <div
          className={classNames('flex justify-center', {
            'flex-col lg:flex-row lg:gap-18': !previewView,
            'flex-row gap-18': isDesktopView,
            'flex-col': isMobileView,
          })}
        >
          <div
            className={classNames('flex flex-col gap-1', {
              'items-center text-center lg:items-start lg:text-start': !previewView,
              'items-start text-start': isDesktopView,
              'items-center text-center': isMobileView,
            })}
          >
            <div className="flex flex-row max-w-xl">
              <Typography className="" variant="h1" style={hubTitleColor}>
                {hub.title}
              </Typography>
            </div>
            <span style={hubDescriptionColor}>
              <Markdown
                content={hub.longDescription}
                className="max-w-lg md-render !text-start"
                defaultStyles={false}
              />
            </span>
            <div>
              <FavoriteButton
                favorited={orgHub?.favorited}
                text
                className="mt-3 !items-start"
                onFavorite={favoriteHub}
                onUnfavorite={unfavoriteHub}
                style={hubButtonBackgroundColor}
              />
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="relative">
              <img
                className="object-cover rounded-sm max-h-[300px]"
                src={thumbnail}
                alt={`Hub ${hub.title} thumbnail`}
              />
              {hasVideoThumbnail && <PlayVideoButton imageUrl={hub.headerImageUrl} />}
            </div>
            <Typography variant="sm" className="my-5 text-center" style={hubDescriptionColor}>
              This content provided by:
            </Typography>
            <img className="object-contain w-full max-h-[100px]" src={hub.logoImageUrl} alt={`${hub.author} logo`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubHeaderPreview;
